import React, { useState, useEffect, forwardRef, useImperativeHandle, useContext } from 'react'
import {
  Table,
  Modal,
  message,
  Input,
  Checkbox,
  Button
} from 'antd'
import { DollarOutlined, FilterOutlined } from '@ant-design/icons'

import './cashCount.scss'
import Spinner from '../../../components/common/spinner/spinner'
import CashCountModal from './cashCountModal/cashCountModal'
import DenominationModal from '../../../components/feature/denominationModal/denominationModal'

import {
  getTableColumns,
  getTableProps,
  handleExportPDF,
  handleExportCSV,
  getUniqueValues,
  formatCreatedAtDateTime
} from './cashCountUtils'

// Import API function for fetching cash counts
import { fetchCashCounts } from '../../../api/cashCountApiService/cashCountApiUtils'
import { useMsal } from '@azure/msal-react'
// Import store context
import { StoreContext } from '../../../context/storeContext'

// Update component to use forwardRef to expose methods to parent
const CashCount = forwardRef(({ selectedDate, onDataLoaded, shouldLoadData = false }, ref) => {
  // State for data
  const [data, setData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [loading, setLoading] = useState(false)
  const [visibleData, setVisibleData] = useState([]) // New state for visible data with updated serial numbers

  const [dataLoadedForDate, setDataLoadedForDate] = useState(null)

  // State for modals
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false)
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [denominationModalVisible, setDenominationModalVisible] = useState(false)
  const [currentRecord, setCurrentRecord] = useState(null)
  const [selectedRecord, setSelectedRecord] = useState(null)

  // State for system cash on hand from parent component
  const [systemCashOnHand, setSystemCashOnHand] = useState(0)

  // New states for filter functionality
  const [tableFilters, setTableFilters] = useState({})
  const [checkerNames, setCheckerNames] = useState([])
  const [createdByNames, setCreatedByNames] = useState([])

  // Get the MSAL instance for authentication
  const { instance } = useMsal()

  // Get store information from store context
  const storeContext = useContext(StoreContext)
  const { name: currentStoreName } = storeContext || {}

  // Set store name in window object for export functions to access
  useEffect(() => {
    if (currentStoreName) {
      window.currentStoreName = currentStoreName
    }
  }, [currentStoreName])

  // Effect to get system cash on hand from window object
  useEffect(() => {
    // Check if window object contains systemCashOnHand data
    if (window.pettySystemCashOnHand !== undefined) {
      setSystemCashOnHand(window.pettySystemCashOnHand || 0)
    }

    // Set up listener for updates to system cash on hand
    const handleSystemCashUpdate = (e) => {
      if (e.detail && e.detail.systemCashOnHand !== undefined) {
        setSystemCashOnHand(e.detail.systemCashOnHand)
      }
    }

    // Add event listener
    window.addEventListener('systemCashOnHandUpdated', handleSystemCashUpdate)

    // Clean up
    return () => {
      window.removeEventListener('systemCashOnHandUpdated', handleSystemCashUpdate)
    }
  }, [])

  // Function to create a consistent record number format: YYYYMM-NNNN
  const generateRecordNumber = (date, index) => {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const sequentialNumber = (index + 1).toString().padStart(4, '0')
    return `${year}${month}-${sequentialNumber}`
  }

  const formatDenominationDisplay = (value) => {
    const denomValue = parseFloat(value)
    if (denomValue >= 1) {
      return denomValue.toFixed(0)
    } else {
      return denomValue.toFixed(2)
    }
  }

  // Utility function to format currency with decimals
  const formatCurrencyWithDecimals = (value) => {
    if (value === null || value === undefined) return ''
    return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  // Function to process denomination data from API
  const processDenominations = (apiDenominations) => {
    if (!apiDenominations || !Array.isArray(apiDenominations)) {
      return []
    }

    // Convert API data to consistent format and sort by value (descending)
    return apiDenominations
      .map(item => ({
        value: parseFloat(item.denomination || item.value || 0),
        count: item.quantity || item.count || 0,
        total: item.total || ((parseFloat(item.denomination || item.value || 0)) * (item.quantity || item.count || 0))
      }))
      .filter(item => item.value > 0) // Remove invalid denominations
      .sort((a, b) => b.value - a.value) // Sort by denomination value (descending)
  }

  const mapApiResponseToTableData = (cashCountLogs) => {
    // Use response order as-is - preserve the order received from API
    return cashCountLogs.map((item, index) => {
      // Format date and time from createdAt
      const dateTime = new Date(item.createdAt)

      // Calculate the surplus or shortage
      const discrepancy = item.totalAmount - systemCashOnHand
      // We'll set a surplus or shortage value based on the sign
      const surplus = discrepancy > 0 ? discrepancy : 0.00
      const shortage = discrepancy < 0 ? Math.abs(discrepancy) : 0.00

      // Generate a unique record number based on creation date and a sequential counter
      const recordNumber = generateRecordNumber(dateTime, index)

      const completeDenominations = processDenominations(item.denominations)

      const createdByName = item.createdByUser?.userName || item.createdBy || 'System'
      const createdByEmail = item.createdByUser?.emailId || item.createdByEmail || ''

      const checkerName = item.countedByUser || item.countedBy || 'Unknown'

      return {
        key: index + 1, // Original key stays the same
        originalIndex: index + 1, // Keep track of original index
        backendIndex: index + 1, // Track backend order for sorting when no explicit sort is applied
        serialNo: index + 1, // This will be recalculated when filtered
        recordNumber: recordNumber, // Unique sequential record number (hidden from table)
        countId: item._id || '',
        date: dateTime.toISOString().split('T')[0], // YYYY-MM-DD format (keep for compatibility)
        time: dateTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true }), // keep for compatibility
        checker: checkerName, // This is the name from countedByUser field
        createdBy: createdByName, // This is the logged-in user who created the record
        createdByEmail: createdByEmail, // Email for user matching
        denomination: completeDenominations,
        totalDenomination: item.totalAmount || 0,
        cashOut: systemCashOnHand || 0,
        discrepancy: shortage, // Now only showing shortage as discrepancy
        surplus: surplus,
        notes: item.notes || '',
        storeId: item.storeId,
        createdAtRaw: item.createdAt, // Keep raw for sorting
        dateObj: dateTime,
        ...item,
        createdAt: formatCreatedAtDateTime(dateTime) 
      }
    })
  }

  // Function to clear all filters with proper re-rendering
  const handleClearAllFilters = () => {
    // Clear filter state
    setTableFilters({})

    // Reset filtered data to original data
    setFilteredData([...data])

    // Update visible data with recalculated serial numbers
    const recalculatedData = data.map((item, index) => ({
      ...item,
      serialNo: index + 1
    }))
    setVisibleData(recalculatedData)

    // Force table to clear filters by getting the table element and clicking reset buttons
    setTimeout(() => {
      const filterDropdowns = document.querySelectorAll('.ant-table-filter-dropdown button:last-child')
      filterDropdowns.forEach(button => {
        button.click()
      })
    }, 0)
  }

  // Function to show denomination modal
  const showDenominationModal = (record) => {
    setSelectedRecord(record)
    setDenominationModalVisible(true)
  }

  useImperativeHandle(ref, () => ({
    // Get the latest cash count
    getLatestCashCount: () => {
      if (data && data.length > 0) {
        // Sort by date (newest first)
        const sortedData = [...data].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        )
        return sortedData[0]
      }
      return null
    },

    // Get all cash counts
    getAllCashCounts: () => data,

    // Refresh cash counts
    refreshCashCounts: async () => {
      return loadCashCounts()
    },

    // Check if filters are applied
    hasFiltersApplied: () => hasFiltersApplied(),

    // Clear all filters
    clearAllFilters: () => handleClearAllFilters()
  }))

  // Check if any filters are applied
  const hasFiltersApplied = () => {
    return Object.keys(tableFilters).some(key =>
      tableFilters[key] && tableFilters[key].length > 0
    )
  }

  // Function to load cash counts from API
  const loadCashCounts = async () => {
    try {
      setLoading(true)

      // Get month and year from selectedDate prop
      const month = selectedDate.format('MM')
      const year = selectedDate.format('YYYY')
      const dateKey = `${year}-${month}`

      // Prepare parameters for API call
      const params = {
        month: month,
        year: year,
        countType: 'cashCount',
        status: 'active'
      }

      // Call API to get cash counts
      const response = await fetchCashCounts(instance, params)

      // Check if API returned data
      if (response && response.cashCountLogs && response.cashCountLogs.length > 0) {
        // Map response data to table format and assign unique record numbers
        const formattedData = mapApiResponseToTableData(response.cashCountLogs)

        const renumberedData = formattedData.map((item, index) => ({
          ...item,
          key: index + 1,
          serialNo: index + 1
        }))

        // Extract unique checker names and created by names for filters
        const uniqueCheckerNames = getUniqueValues(renumberedData, 'checker')
        const uniqueCreatedByNames = getUniqueValues(renumberedData, 'createdBy')

        setCheckerNames(uniqueCheckerNames)
        setCreatedByNames(uniqueCreatedByNames)

        // Reset filters when loading new data
        setTableFilters({})

        setData(renumberedData)
        setFilteredData(renumberedData)
        setVisibleData(renumberedData)

        // Pass data to parent component via callback
        if (onDataLoaded) {
          onDataLoaded(response.cashCountLogs)
        }
      } else {
        // If no data, set empty arrays
        console.log('No cash count records received')
        setData([])
        setFilteredData([])
        setVisibleData([])
        setCheckerNames([])
        setCreatedByNames([])

        // Pass empty data to parent
        if (onDataLoaded) {
          onDataLoaded([])
        }
      }

      // Mark data as loaded for this date
      setDataLoadedForDate(dateKey)

      return response // Return the response for promise chaining
    } catch (error) {
      console.error('Error loading cash counts:', error)
      message.error('Failed to load cash counts. Please try again later.')

      // Clear data on error
      setCheckerNames([])
      setCreatedByNames([])

      // Pass empty data to parent on error
      if (onDataLoaded) {
        onDataLoaded([])
      }

      throw error // Rethrow the error for proper promise rejection
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const month = selectedDate.format('MM')
    const year = selectedDate.format('YYYY')
    const dateKey = `${year}-${month}`

    if (shouldLoadData && dataLoadedForDate !== dateKey) {
      loadCashCounts()
    } else if (!shouldLoadData) {
    } else {
    }

    if (dataLoadedForDate && dataLoadedForDate !== dateKey) {
      handleClearAllFilters()
    }
  }, [selectedDate, shouldLoadData])

  useEffect(() => {
    const month = selectedDate.format('MM')
    const year = selectedDate.format('YYYY')
    const dateKey = `${year}-${month}`

    // If this is a new date, reset the loaded state
    if (dataLoadedForDate && dataLoadedForDate !== dateKey) {
      console.log('Date changed, resetting loaded state')
      setDataLoadedForDate(null)
      setData([])
      setFilteredData([])
      setVisibleData([])
    }
  }, [selectedDate])

  // Show details modal
  const showDetailsModal = (record) => {
    const originalRecord = data.find(item => item.key === record.key)
    setCurrentRecord(originalRecord || record)
    setIsDetailsModalVisible(true)
  }

  // Handle successful creation of new cash count
  const handleCashCountCreated = () => {
    setIsCreateModalVisible(false)

    console.log('handleCashCountCreated called, refreshing data')
    loadCashCounts()
      .then(() => console.log('Cash counts refreshed after creation'))
      .catch(err => console.error('Error refreshing cash counts after creation:', err))
  }

  const handleTableChange = (pagination, filters, sorter) => {
    // Save the filters to state for later reference
    setTableFilters(filters)

    // Always apply filters against the original data to handle consecutive filters correctly
    let newFilteredData = [...data]

    // Apply all active filters manually to ensure consistent behavior
    Object.keys(filters).forEach(key => {
      const activeFilter = filters[key]
      if (activeFilter && activeFilter.length > 0) {
        // Different handling based on column type
        if (key === 'date' || key === 'createdAt') {
          // Date range filter
          if (activeFilter[0] && activeFilter[0].length === 2) {
            const startDate = activeFilter[0][0].startOf('day')
            const endDate = activeFilter[0][1].endOf('day')

            newFilteredData = newFilteredData.filter(record => {
              const recordDate = record.dateObj ? new Date(record.dateObj) : new Date(record.createdAt)
              return recordDate >= startDate && recordDate <= endDate
            })
          }
        } else if (key === 'checker' || key === 'createdBy') {
          // Checker and Created By filter (exact match on one of the selected values)
          newFilteredData = newFilteredData.filter(record =>
            activeFilter.includes(record[key])
          )
        }
      }
    })

    // Apply sorting if specified
    if (sorter && sorter.column) {
      const { order, field } = sorter
      newFilteredData = [...newFilteredData].sort((a, b) => {
        if (order === 'ascend') {
          return a[field] < b[field] ? -1 : a[field] > b[field] ? 1 : 0
        } else {
          return a[field] > b[field] ? -1 : a[field] < b[field] ? 1 : 0
        }
      })
    } else {
      newFilteredData = [...newFilteredData].sort((a, b) => {
        return a.backendIndex - b.backendIndex
      })
    }

    setFilteredData(newFilteredData)

    // Recalculate serial numbers after filtering
    const recalculatedData = newFilteredData.map((item, index) => ({
      ...item,
      serialNo: index + 1
    }))

    setVisibleData(recalculatedData)
  }

  // Function to render clear filters button
  const renderClearFiltersButton = () => {
    // Only show the button if filters are applied
    if (!hasFiltersApplied()) return null

    return (
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        <Button
          onClick={handleClearAllFilters}
          icon={<FilterOutlined />}
          type="default"
          size="small"
        >
          Clear All Filters
        </Button>
      </div>
    )
  }

  // Get table columns using the updated column definition with filters
  const columns = getTableColumns(
    showDetailsModal,
    handleExportPDF,
    handleExportCSV,
    tableFilters,
    checkerNames,
    createdByNames,
    Input,
    Checkbox,
    showDenominationModal, // Add this new parameter
    formatCurrencyWithDecimals // Add this new parameter
  )

  const tableProps = getTableProps(columns)

  const getTableHeight = () => {
    return 'calc(100vh - 590px)'
  }

  // Reference to the table component for programmatic control
  const tableRef = React.useRef(null)

  return (
    <div className="cash-count-wrapper">
      <div className="table-container">
        <div className="table-responsive">
          {renderClearFiltersButton()}
          {loading ? (
            <div className="spinner-container">
              <Spinner size="large" />
            </div>
          ) : data.length > 0 ? (
            <Table
              ref={tableRef}
              className="cash-count-table"
              columns={columns}
              dataSource={visibleData}
              onChange={handleTableChange}
              pagination={false}
              scroll={{
                ...tableProps.scroll,
                y: getTableHeight()
              }}
              sticky={tableProps.sticky}
              rowClassName={() => 'table-row-same-height'}
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row className="summary-row">
                    <Table.Summary.Cell index={0} colSpan={9} className="summary-title">
                      <span className="transaction-count">
                        {filteredData.length} Cash Counts
                      </span>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          ) : (
            <div className="cash-count-container">
              <div className="cash-count-placeholder">
                <div className="cash-count-message">
                  <DollarOutlined className="cash-count-icon" />
                  <p>
                    {currentStoreName && <span className="store-name">{currentStoreName} - </span>}
                    No cash count records yet. Click the "New Cash Count" button to get started.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal for viewing denomination details */}
      <Modal
        title={`${currentStoreName ? currentStoreName + ' - ' : ''}Cash Count Record #${currentRecord?.recordNumber || ''}`}
        open={isDetailsModalVisible}
        onCancel={() => setIsDetailsModalVisible(false)}
        footer={null}
        width={700}
      >
        {currentRecord && (
          <div>
            <div className="modal-details">
              <p><strong>Checker:</strong> {currentRecord.checker}</p>
              <p><strong>Created By:</strong> {currentRecord.createdBy}</p>
              <p><strong>Created At:</strong> {currentRecord.createdAt}</p>
            </div>

            <h3>CASH ON HAND</h3>
            <Table
              className="modal-denomination-table"
              bordered
              size="small"
              pagination={false}
              dataSource={currentRecord.denomination}
              rowKey="value"
              rowClassName={(record) => record.count > 0 ? 'highlight-row' : ''}
              columns={[
                {
                  title: 'DENOMINATION',
                  dataIndex: 'value',
                  key: 'value',
                  render: (text) => <span>₱{formatDenominationDisplay(text)}</span>
                },
                {
                  title: 'COUNT',
                  dataIndex: 'count',
                  key: 'count',
                  align: 'right'
                },
                {
                  title: 'TOTAL AMT',
                  dataIndex: 'total',
                  key: 'total',
                  align: 'right',
                  render: (text) => <span>₱{text.toFixed(2)}</span>
                }
              ]}
              summary={() => (
                <Table.Summary>
                  <Table.Summary.Row className="summary-total-row">
                    <Table.Summary.Cell index={0}>
                      <strong>TOTAL COH</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}></Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <strong>₱{currentRecord.totalDenomination.toFixed(2)}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />

            <div className="summary-section">
              <table className="summary-table">
                <tbody>
                  <tr>
                    <td>Ideal Petty Cash Amt</td>
                    <td>₱{systemCashOnHand.toFixed(2)}</td>
                  </tr>
                  {(() => {
                    // Calculate discrepancy as totalDenomination - systemCashOnHand
                    const discrepancyAmount = currentRecord.totalDenomination - systemCashOnHand

                    // Show different rows based on whether we have surplus or shortage
                    if (discrepancyAmount < 0) {
                      // Show discrepancy for shortage (negative difference)
                      return (
                        <tr>
                          <td>Discrepancy</td>
                          <td className="negative-discrepancy">
                            ₱{Math.abs(discrepancyAmount).toFixed(2)}
                          </td>
                        </tr>
                      )
                    } else if (discrepancyAmount > 0) {
                      // Show surplus for excess (positive difference)
                      return (
                        <tr>
                          <td>Surplus</td>
                          <td className="positive-surplus">
                            ₱{discrepancyAmount.toFixed(2)}
                          </td>
                        </tr>
                      )
                    } else {
                      // Show zero discrepancy when amounts match
                      return (
                        <tr>
                          <td>Discrepancy</td>
                          <td>₱0.00</td>
                        </tr>
                      )
                    }
                  })()}
                </tbody>
              </table>
            </div>

            {currentRecord.notes && (
              <div className="notes-section">
                <strong>Notes:</strong>
                <p>{currentRecord.notes}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal for creating a new cash count */}
      <CashCountModal
        visible={isCreateModalVisible}
        onClose={handleCashCountCreated}
        onSuccess={loadCashCounts}
      />

      {/* Denomination Modal - Using the reusable component */}
      <DenominationModal
        visible={denominationModalVisible}
        onClose={() => setDenominationModalVisible(false)}
        title={`${currentStoreName ? currentStoreName + ' - ' : ''}Cash Count - ${selectedRecord?.recordNumber || ''}`}
        denominationData={selectedRecord?.denomination || []}
        systemCashOnHand={systemCashOnHand}
        actualCashOnHand={selectedRecord?.totalDenomination || 0}
        showComparison={true}
      />
    </div>
  )
})

export default CashCount